import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useQuery, useMutation } from '@apollo/client';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import Select from 'react-select';
import { ReactComponent as DragIcon } from '../../shared/icons/drag.svg';
import { Slideout } from '../slideout';
import { CustomFieldItem } from './CustomFieldItem';
import {
  GET_TEAM_CONTACT_FIELD_SCHEMAS,
  CREATE_TEAM_CONTACT_FIELD_SCHEMA,
  REORDER_TEAM_CONTACT_FIELD_SCHEMAS,
} from '../../api/contacts/customFieldsApi';
import styles from './CustomFieldManager.module.scss';

const selectStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    fontSize: '14px',
    borderColor: state.isFocused ? '#2196f3' : 'lightgray',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(33, 150, 243, 0.1)' : null,
    '&:hover': {
      borderColor: state.isFocused ? '#2196f3' : 'lightgray',
    },
  }),
  indicatorSeparator: (provided: any) => ({
    ...provided,
    display: 'none',
  }),
  menu: (provided: any) => ({
    ...provided,
    zIndex: 10000,
  }),
  menuPortal: (provided: any) => ({
    ...provided,
    zIndex: 10000,
  }),
  menuList: (provided: any) => ({
    ...provided,
    maxHeight: '200px',
  }),
  option: (provided: any) => ({
    ...provided,
    fontSize: '14px',
  }),
  singleValue: (provided: any) => ({
    ...provided,
    fontSize: '14px',
  }),
  placeholder: (provided: any) => ({
    ...provided,
    fontSize: '14px',
    color: '#999',
  }),
};

interface CustomFieldManagerSlideoutProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
}

interface TeamContactFieldSchema {
  id: string;
  fieldKey: string;
  fieldLabel: string;
  fieldType: string;
  displayOrder: number;
  inUse: boolean;
  usageCount: number;
}

const FIELD_TYPE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
];

interface DraggableFieldProps {
  field: TeamContactFieldSchema;
  index: number;
  moveField: (dragIndex: number, hoverIndex: number) => void;
  onUpdate: () => void;
  onDelete: () => void;
}

const DraggableField: React.FC<DraggableFieldProps> = ({ field, index, moveField, onUpdate, onDelete }) => {
  const [{ isDragging }, drag, preview] = useDrag({
    type: 'FIELD',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'FIELD',
    hover: (item: { index: number }) => {
      if (item.index !== index) {
        moveField(item.index, index);
        item.index = index;
      }
    },
  });

  return (
    <div ref={(node) => preview(drop(node))} className={isDragging ? styles.dragging : ''}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div ref={drag} className={styles.dragHandle}>
          <DragIcon style={{ width: '1.25rem', height: '1.25rem' }} />
        </div>
        <div style={{ flex: 1 }}>
          <CustomFieldItem field={field} onUpdate={onUpdate} onDelete={onDelete} />
        </div>
      </div>
    </div>
  );
};

export const CustomFieldManagerSlideout: React.FC<CustomFieldManagerSlideoutProps> = ({
  isOpen,
  onClose,
  teamId,
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldType, setNewFieldType] = useState('text');
  const [fields, setFields] = useState<TeamContactFieldSchema[]>([]);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { data, loading, refetch } = useQuery(GET_TEAM_CONTACT_FIELD_SCHEMAS, {
    variables: { teamId },
    skip: !teamId || !isOpen,
  });

  // Sync fields state with query data
  useEffect(() => {
    if (data?.teamContactFieldSchemas) {
      setFields([...data.teamContactFieldSchemas]);
    }
  }, [data]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const [createField, { loading: creating }] = useMutation(CREATE_TEAM_CONTACT_FIELD_SCHEMA, {
    refetchQueries: [
      {
        query: GET_TEAM_CONTACT_FIELD_SCHEMAS,
        variables: { teamId },
      },
    ],
  });

  const [reorderFields] = useMutation(REORDER_TEAM_CONTACT_FIELD_SCHEMAS, {
    refetchQueries: [
      {
        query: GET_TEAM_CONTACT_FIELD_SCHEMAS,
        variables: { teamId },
      },
    ],
  });

  const handleAddField = async () => {
    if (!newFieldLabel.trim()) return;

    try {
      const { data } = await createField({
        variables: {
          teamId,
          fieldLabel: newFieldLabel,
          fieldType: newFieldType,
        },
      });

      if (data?.createTeamContactFieldSchema?.errors?.length > 0) {
        alert(data.createTeamContactFieldSchema.errors.join('\n'));
        return;
      }

      setNewFieldLabel('');
      setNewFieldType('text');
      setShowAddForm(false);
      refetch();
    } catch (error) {
      console.error('Error creating field:', error);
      alert('Failed to create field');
    }
  };

  const saveFieldOrder = useCallback(
    async (newFields: TeamContactFieldSchema[]) => {
      try {
        await reorderFields({
          variables: {
            teamId,
            orderedIds: newFields.map((f) => f.id),
          },
        });
      } catch (error) {
        console.error('Error reordering fields:', error);
        // Silently fail - user can retry by dragging again
      }
    },
    [teamId, reorderFields]
  );

  const moveField = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const newFields = [...fields];
      const [removed] = newFields.splice(dragIndex, 1);
      newFields.splice(hoverIndex, 0, removed);
      setFields(newFields);

      // Auto-save order with debouncing
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        saveFieldOrder(newFields);
      }, 300); // Wait 300ms after last drag before saving
    },
    [fields, saveFieldOrder]
  );

  return (
    <Slideout isOpen={isOpen} onClose={onClose} title="Manage Contact Fields" width="medium">
      <div>
        {showAddForm && (
          <div className={styles.addFieldForm}>
            <Form.Group className="mb-3">
              <Form.Label>
                Field Label <span className="required">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={newFieldLabel}
                onChange={(e) => setNewFieldLabel(e.target.value)}
                placeholder="e.g., Driver's License Number"
                autoFocus
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Field Type</Form.Label>
              <Select
                options={FIELD_TYPE_OPTIONS}
                value={FIELD_TYPE_OPTIONS.find((o) => o.value === newFieldType) || FIELD_TYPE_OPTIONS[0]}
                onChange={(option: any) => setNewFieldType(option ? option.value : 'text')}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                menuPlacement="auto"
              />
            </Form.Group>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setShowAddForm(false);
                  setNewFieldLabel('');
                  setNewFieldType('text');
                }}
                disabled={creating}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleAddField}
                disabled={creating || !newFieldLabel.trim()}
              >
                {creating ? 'Adding...' : 'Add Field'}
              </Button>
            </div>
          </div>
        )}

        {!showAddForm && fields.length > 0 && (
          <div className={styles.addFieldButtonContainer}>
            <Button
              variant="primary"
              onClick={() => setShowAddForm(true)}
              data-qa="add-custom-field-button"
            >
              + Add Custom Field
            </Button>
          </div>
        )}

        <DndProvider backend={HTML5Backend}>
          <div className={styles.fieldsList}>
            {loading && <p>Loading custom fields...</p>}

            {!loading && fields.length === 0 && !showAddForm && (
              <div className={styles.emptyState}>
                <h4>No Custom Fields</h4>
                <p>Create custom fields to capture additional information specific to your team.</p>
                <Button
                  variant="primary"
                  onClick={() => setShowAddForm(true)}
                  data-qa="add-custom-field-button-empty"
                >
                  + Add Custom Field
                </Button>
              </div>
            )}

            {fields.map((field, index) => (
              <DraggableField
                key={field.id}
                field={field}
                index={index}
                moveField={moveField}
                onUpdate={refetch}
                onDelete={refetch}
              />
            ))}
          </div>
        </DndProvider>
      </div>
    </Slideout>
  );
};
